import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import type { ReschedulePayload, RescheduleResponse } from '../types';

type ServiceUserEnvelope<T> = {
  message?: string;
  status?: string;
  responseBody?: T;
  errorCode?: number;
  errorMessage?: string | null;
};

const ENDPOINT = '/user/v1/trainer-slots/parent-connect/request/reschedule';

// Parent-side counter-reschedule. The trainer's reschedule cap stays
// pinned to the original BatchSessions row (this call does NOT bump
// `reschedule_request_count`); see
// `BatchSchedulingService/services/parent_connect_lifecycle.py:_reschedule_pending_parent_connect_core`.
export const rescheduleParentConnectRequest = async (
  payload: ReschedulePayload
): Promise<RescheduleResponse> => {
  const data: ServiceUserEnvelope<RescheduleResponse> = await axios.post(
    ENDPOINT,
    payload
  );
  return data?.responseBody ?? {};
};

export const rescheduleParentConnectRequestMockHandler = http.post(
  `${env.API_URL}${ENDPOINT}`,
  async ({ request }) => {
    await networkDelay();
    // Echo the chosen new slot back so the success view in dev
    // shows the parent's actual selection. Falls back to a sensible
    // "now + 36h" if body parsing fails.
    let startEpoch = Math.floor(Date.now() / 1000) + 36 * 60 * 60;
    let endEpoch = startEpoch + 1800;
    let sessionId = 'mock-session-id';
    try {
      const body = (await request.json()) as Partial<ReschedulePayload>;
      if (typeof body.session_id === 'string' && body.session_id.length > 0) {
        sessionId = body.session_id;
      }
      const parsedStart = Number(body.new_start_time);
      if (Number.isFinite(parsedStart) && parsedStart > 0) {
        startEpoch = parsedStart;
      }
      const parsedEnd = Number(body.new_end_time);
      if (Number.isFinite(parsedEnd) && parsedEnd > 0) {
        endEpoch = parsedEnd;
      }
    } catch {
      /* swallow - dev-only mock, keep defaults */
    }
    return HttpResponse.json<ServiceUserEnvelope<RescheduleResponse>>({
      status: 'SUCCESS',
      message: 'Parent-connect request rescheduled',
      errorCode: 0,
      errorMessage: null,
      responseBody: {
        session_id: sessionId,
        start_time: startEpoch,
        end_time: endEpoch,
        timeZone: 'Asia/Kolkata',
        session_status: 'pending',
      },
    });
  }
);
